import { useState, useEffect, useRef, useCallback } from 'react';
import { isDateAfter, isDateBefore, formatDate } from '../../../../helpers/dateUtils';
import alumnosApiService from '../../../../services/http/alumnosApiService';
import recibosApiService from '../../../../services/http/recibosApiService';
import logger from '../../../../services/logger';

const PAGE_SIZE = 100;

export default function useFormularioRecibos() {
  const [formationList, setFormationList] = useState([]);
  const [isFormSubmittable, setIsFormSubmittable] = useState(true);
  const [isTableNotLoading, setIsTableNotLoading] = useState(true);
  const [formationCenter, setFormationCenter] = useState([]);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFinal, setFechaFinal] = useState(null);
  const [fechaError, setFechaError] = useState('');
  const [headquarter, setHeadquarter] = useState('CEDESCA');
  const [idRecibo, setIdRecibo] = useState({ data: '', error: '' });
  const [tipoCliente, setTipoCliente] = useState('0');
  const [estadoRecibo, setEstadoRecibo] = useState([]);
  const [formaPago, setFormaPago] = useState([]);
  const [data, setData] = useState(null);
  const [name, setName] = useState({ data: '', error: '' });
  const [lastname, setLastname] = useState({ data: '', error: '' });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const dataCentrosFormacion = await alumnosApiService.cargarCentrosFormacion({
          empresaId: 'CEDESCA',
        });
        const list = [...dataCentrosFormacion];
        list.unshift({ Id: '', Nombre: 'Todos' });
        const opcionesRenombradas = list.map((opcion) => ({
          value: opcion.Id,
          label: opcion.Nombre,
        }));
        setFormationList(opcionesRenombradas);
        setIsFormSubmittable(true);
      } catch (error) {
        setFormationList([]);
        setIsFormSubmittable(false);
      }
    };
    fetch();
  }, []);

  const handleHeadquarter = (e) => {
    setHeadquarter(e.target.value);
  };

  const handleClientType = (e) => {
    setTipoCliente(e.target.value);
  };

  const handleEstadoRecibo = (selected) => {
    setEstadoRecibo(selected || []);
  };

  const handleFormaPago = (selected) => {
    setFormaPago(selected || []);
  };

  const handleFechaInicio = (value) => {
    const nuevaFechaInicio = value ? (value instanceof Date ? value : new Date(value)) : null;
    if (!nuevaFechaInicio) {
      setFechaFinal(null);
      setFechaError('');
    } else if (fechaFinal && isDateAfter(nuevaFechaInicio, fechaFinal)) {
      setFechaError('La fecha de inicio no puede ser posterior a la fecha final.');
    } else {
      setFechaError('');
    }
    setFechaInicio(nuevaFechaInicio);
  };

  const handleFechaFinal = (value) => {
    const nuevaFechaFinal = value instanceof Date ? value : new Date(value);
    if (fechaInicio && isDateBefore(nuevaFechaFinal, fechaInicio)) {
      setFechaError('La fecha final no puede ser anterior a la fecha de inicio.');
    } else {
      setFechaError('');
    }
    setFechaFinal(nuevaFechaFinal);
  };

  const handleFormationCenter = (selected) => {
    setFormationCenter(selected);
  };

  const buildParams = () => ({
    empresaId: headquarter,
    nombre: name.data,
    apellido: lastname.data,
    idRecibo: idRecibo.data,
    tipoCliente,
    estadoRecibo: estadoRecibo.map((estado) => estado.value).join(','),
    formaPago: formaPago.map((forma) => forma.value).join(','),
    fechaI: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : '',
    fechaF: fechaFinal ? formatDate(fechaFinal, 'yyyy-MM-dd') : '',
    centrosFormacion: formationCenter,
    excel: false,
  });

  const handleOnSubmit = async (e) => {
    e.preventDefault();
    setIsTableNotLoading(false);
    offsetRef.current = 0;
    const params = { ...buildParams(), limit: PAGE_SIZE, offset: 0 };
    lastParamsRef.current = params;
    try {
      const actualData = await recibosApiService.buscarListadoRecibos(params);
      setData([...actualData]);
      setHasMore(actualData.length >= PAGE_SIZE);
      offsetRef.current = actualData.length;
    } catch (error) {
      setData([]);
      setHasMore(false);
    } finally {
      setIsTableNotLoading(true);
    }
  };

  const loadMore = useCallback(async () => {
    if (!lastParamsRef.current || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const params = { ...lastParamsRef.current, offset: offsetRef.current };
      const moreData = await recibosApiService.buscarListadoRecibos(params);
      if (moreData.length > 0) {
        setData((prev) => [...(prev || []), ...moreData]);
        offsetRef.current += moreData.length;
      }
      setHasMore(moreData.length >= PAGE_SIZE);
    } catch (error) {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore]);

  const downloadExcel = async (e) => {
    e.preventDefault();
    setIsTableNotLoading(false);
    const params = {
      empresaId: headquarter,
      nombre: name.data,
      apellido: lastname.data,
      idRecibo: idRecibo.data,
      tipoCliente,
      estadoRecibo: estadoRecibo.map((estado) => estado.value).join(','),
      formaPago: formaPago.map((forma) => forma.value).join(','),
      fechaI: fechaInicio ? formatDate(fechaInicio, 'yyyy-MM-dd') : '',
      fechaF: fechaFinal ? formatDate(fechaFinal, 'yyyy-MM-dd') : '',
      centrosFormacion: formationCenter,
      excel: true,
    };
    try {
      const dataSolicitudes = await recibosApiService.buscarListadoRecibos(params);
      const url2 = window.URL.createObjectURL(dataSolicitudes);
      const a = document.createElement('a');
      a.href = url2;
      a.download = 'document.xlsx';
      a.click();
      window.URL.revokeObjectURL(url2);
    } catch (error) {
      logger.error('Error al descargar el archivo:', error);
    } finally {
      setIsTableNotLoading(true);
    }
  };

  const handleNameOnChange = (e) => {
    if (e.target.value.trim().length === 0) {
      setName({ data: '', error: '' });
      setIsFormSubmittable(true);
    } else if (!e.target.value.trim().match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setName({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (e.target.value.trim().length === 1) {
      setName({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setName({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleLastNameOnChange = (e) => {
    if (!e.target.value.trim().match(/^(?![\s.]+$)[a-zA-ZÀ-ÿ\s.]*$/)) {
      setLastname({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else if (e.target.value.trim().length === 1) {
      setLastname({ data: '', error: 'length' });
      setIsFormSubmittable(false);
    } else {
      setLastname({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  const handleIDOnChange = (e) => {
    if (e.target.value.trim().length === 0) {
      setIdRecibo({ data: '', error: '' });
      setIsFormSubmittable(true);
    } else if (e.target.value.trim().match(/^[^0-9]*$/)) {
      setIdRecibo({ data: '', error: 'format' });
      setIsFormSubmittable(false);
    } else {
      setIdRecibo({ data: e.target.value.trim(), error: '' });
      setIsFormSubmittable(true);
    }
  };

  return {
    formationList,
    isFormSubmittable,
    isTableNotLoading,
    formationCenter,
    fechaInicio,
    fechaFinal,
    fechaError,
    headquarter,
    idRecibo,
    tipoCliente,
    estadoRecibo,
    formaPago,
    data,
    name,
    lastname,
    handleHeadquarter,
    handleClientType,
    handleEstadoRecibo,
    handleFormaPago,
    handleFechaInicio,
    handleFechaFinal,
    handleFormationCenter,
    handleOnSubmit,
    downloadExcel,
    handleNameOnChange,
    handleLastNameOnChange,
    handleIDOnChange,
    loadMore,
    isLoadingMore,
    hasMore,
  };
}
